/**
 * GKC Input/Group/Widget Conditional Logic
 */

(function(Charcoal, $) {

    const DEBUG = false;

    const STATE_SELECTOR_PREFIX  = 'js-condition-state';
    const TARGET_SELECTOR_PREFIX = 'js-condition-target';
    const LAYOUT_SELECTOR_PREFIX = 'js-condition-layout';

    /**
     * Charcoal Conditional Logic using CSS classes
     *
     * Provides custom support for conditional logic to show/hide
     * properties based on the value of other properties.
     *
     * Notes:
     * 1. (MINIMAL TESTING) This module should be able to support any input value
     *    as state, enclosed structures to prevent leaks, and multiple forms.
     * 2. (MINIMAL TESTING) This module should be able to support multilingual inputs
     *    and targets.
     * 3. Given the complex logic and limited customizability/observability
     *    of modals/dialogs (such as quick forms) and any initial states of
     *    conditional fields in a modal, this module will attempt to resolve
     *    0.9 seconds after the modal is shown.
     *
     * This module expects one field to be the condition (state)
     * and zero or more fields to be the targets of that condition:
     *
     * - `js-condition-state-<stateKey>`: Apply to the input that controls
     *   the target elements.
     * - `js-condition-target-<stateKey>-<!negate><stateValue>`: Apply to the target
     *   elements that should match a given value.
     *
     * Example: Toggle between `internalLink` and `externalLink` according
     * to the value of `linkType`.
     *
     * ```json
     * {
     *     "properties": {
     *         "linkType": {
     *             "input_class": "js-condition-state-linkType"
     *         },
     *         "internalLink": {
     *             "form_group_css_class": "js-condition-target-linkType-[internal]"
     *         },
     *         "externalLink": {
     *             "form_group_css_class": "js-condition-target-linkType-[external]"
     *         }
     *     }
     * }
     * ```
     */
    class ConditionalLogic {
        constructor() {
            const eventListenerOptions = {
                capture: true,
                passive: true,
            };

            this.handleControlStateChangeEvent ??= (event) => this.dispatchControlState(event.target);

            this.handleDelayedDOMMutationEvent ??= (event) => setTimeout(
                () => this.refreshTargetsAndControlsInNode(event.target),
                900
            );

            document.addEventListener('change', this.handleControlStateChangeEvent, eventListenerOptions);

            $(document).on('shown.bs.modal', this.handleDelayedDOMMutationEvent);

            document.addEventListener('app.conditionallogic.refresh', (event) => {
                const targetNode = (event.target instanceof Element || event.target instanceof Document) ? event.target : document;
                this.refreshTargetsAndControlsInNode(targetNode);
            });

            this.refreshTargetsAndControlsInNode(document);
        }

        /**
         * Re-evaluates all state controls and nested targets within the given container.
         *
         * @param {Element|Document} container
         */
        refreshTargetsAndControlsInNode(container = document) {
            DEBUG && console.group('ConditionalLogic.refreshTargetsAndControlsInNode');
            DEBUG && console.log('Container:', container);

            if (!container || !(container instanceof Element || container instanceof Document)) {
                return;
            }

            const stateContainer = this.findStateContainer(container) || document;

            // 1. Dispatch all state controls within or at the container
            this.dispatchControlStatesFromNode(container);

            // 2. Re-evaluate any target elements within the container by dispatching their controlling states
            const nestedTargets = container.querySelectorAll(`[class*="${TARGET_SELECTOR_PREFIX}"]`);
            const TARGET_KEY_REGEXP = new RegExp(`^${TARGET_SELECTOR_PREFIX}-([^-]+)-`);
            const processedKeys = new Set();

            nestedTargets.forEach((nestedTarget) => {
                for (const className of nestedTarget.classList) {
                    const match = className.match(TARGET_KEY_REGEXP);
                    if (match && match[1] && !processedKeys.has(match[1])) {
                        const stateKey = match[1];
                        processedKeys.add(stateKey);
                        const stateControls = stateContainer.querySelectorAll(`[class*="${STATE_SELECTOR_PREFIX}-${stateKey}"]`);
                        stateControls.forEach((sc) => this.dispatchControlState(sc));
                    }
                }
            });

            DEBUG && console.groupEnd();
        }

        /**
         * Find all elements with any state selectors within the given container,
         * and dispatch each element's states.
         *
         * @param {Element|Document} container
         */
        dispatchControlStatesFromNode(container = document) {
            DEBUG && console.group('ConditionalLogic.dispatchControlStatesFromNode');
            DEBUG && console.log('Container:', container);

            if (!container || !(container instanceof Element || container instanceof Document)) {
                return;
            }

            const STATE_CLASS_SELECTOR = `[class*="${STATE_SELECTOR_PREFIX}"]`;

            if (container instanceof Element && container.matches(STATE_CLASS_SELECTOR)) {
                this.dispatchControlState(container);
            }

            const stateControls = container.querySelectorAll(STATE_CLASS_SELECTOR);

            stateControls.forEach((stateControl) => this.dispatchControlState(stateControl));

            DEBUG && console.groupEnd();
        }

        /**
         * Dispatch each state on the given element.
         *
         * @param {Element} stateElement
         */
        dispatchControlState(stateElement) {
            if (!stateElement || !(stateElement instanceof Element)) {
                return;
            }

            const STATE_REGEXP = new RegExp(`^${STATE_SELECTOR_PREFIX}-([^-]+)$`);

            // Collect all elements from stateElement up to the form / state container that might have state classes,
            // plus stateElement itself and any direct control wrappers.
            const candidates = [stateElement];

            let parent = stateElement.parentElement;
            while (parent && !parent.matches('form, .c-structure-property, body')) {
                if (parent.className && typeof parent.className === 'string' && parent.className.includes(STATE_SELECTOR_PREFIX)) {
                    candidates.push(parent);
                }
                parent = parent.parentElement;
            }

            let formControl = this.isFormControl(stateElement)
                ? stateElement
                : stateElement.querySelector('input, select, textarea');

            const controlForDispatch = formControl || stateElement;
            const matchedStateKeys = new Set();

            for (const candidate of candidates) {
                for (const className of candidate.classList) {
                    const matches = className.match(STATE_REGEXP);
                    if (matches && matches[1]) {
                        matchedStateKeys.add(matches[1]);
                    }
                }
            }

            for (const stateKey of matchedStateKeys) {
                this.dispatchControlStateToTargets(controlForDispatch, stateKey);
            }
        }

        /**
         * Dispatch the given state and the state's element
         * to all related target elements.
         *
         * @param {Element} stateControl
         * @param {string}  stateKey
         */
        dispatchControlStateToTargets(stateControl, stateKey) {
            DEBUG && console.group('ConditionalLogic.dispatchControlStateToTargets:', stateKey);
            DEBUG && console.log('stateControl:', stateControl);

            const stateContainer = this.findStateContainer(stateControl);
            DEBUG && console.log('stateContainer:', stateContainer);

            const stateLocale = stateControl.closest('[data-lang]')?.dataset?.lang;
            DEBUG && console.log('stateLocale:', stateLocale);

            const stateValues = this.findStateValues(stateControl, stateLocale, stateContainer);
            DEBUG && console.log('stateValues:', stateValues);

            const stateContext = new StateContext(stateKey, stateValues, stateControl, stateContainer);

            const TARGET_SELECTOR  = `${TARGET_SELECTOR_PREFIX}-${stateKey}`;
            const TARGET_REGEXP   = new RegExp(`^${TARGET_SELECTOR}-\\[(!)?(\\?)?(.+?)(\\?)?\\]$`);

            const TARGET_CLASS_SELECTOR = `[class*="${TARGET_SELECTOR}"]`;
            const TARGET_LANG_SELECTOR  = stateLocale
                ? `.form-field[lang|="${stateLocale}"] `
                : '';

            const beforeChangeEvent = new CustomEvent('app.conditionallogic.beforechange', {
                bubbles: true,
                detail:  stateContext,
            });
            stateControl.dispatchEvent(beforeChangeEvent);

            const targetElements = stateContainer.querySelectorAll(TARGET_LANG_SELECTOR + TARGET_CLASS_SELECTOR);

            targetElements.forEach(
                (targetElement) => this.handleStateOnTarget(targetElement, TARGET_REGEXP, stateContext)
            );

            DEBUG && console.groupEnd();
        }

        /**
         * Traverses the element and its parents until it finds a node
         * that matches the specified CSS selector or endpoint.
         *
         * @param  {Element}  el
         * @param  {string}   selectors
         * @param  {?Element} until
         * @return {?Element}
         */
        findClosestUntil(el, selectors, until = null) {
            do {
                if (el.matches(selectors) || (until && el === until)) {
                    return el;
                }

                el = el.parentElement || el.parentNode;
            } while (el !== null && el.nodeType === 1);

            return null;
        }

        /**
         * Finds the closest container to a given a state or condition element.
         *
         * @param  {Element|Document} element
         * @return {Element|Document}
         */
        findStateContainer(element) {
            if (!element || !(element instanceof Element)) {
                return document;
            }
            return element.closest('.c-structure-property,form') || document;
        }

        /**
         * @param  {Element} stateControl
         * @param  {?string} [stateLocale]
         * @param  {Element|Document} [stateContainer]
         * @return {Element[]}
         */
        findStateInputs(stateControl, stateLocale = null, stateContainer = document) {
            if (!stateControl || !(stateControl instanceof Element)) {
                return [];
            }

            stateLocale ??= stateControl.closest('[data-lang]')?.dataset?.lang;

            // Hack to support inputs that delegate the "input_class"
            // to a parent element.
            if (!this.isFormControl(stateControl)) {
                const controls = [];
                stateControl.querySelectorAll('input,select,textarea').forEach(
                    (control) => {
                        controls.push(...this.findStateInputs(control, stateLocale, stateContainer));
                    }
                );

                return controls;
            }

            if (stateControl instanceof HTMLSelectElement) {
                return Array.from(stateControl.selectedOptions);
            }

            if (stateControl.name) {
                const STATE_NAME_SELECTOR = `[name="${CSS.escape(stateControl.name)}"]`;
                const STATE_LANG_SELECTOR = stateLocale
                    ? `.form-field[lang|="${stateLocale}"] `
                    : '';
                const STATE_SELECTOR = STATE_LANG_SELECTOR + STATE_NAME_SELECTOR;

                if (stateControl.type === 'checkbox' || stateControl.type === 'radio') {
                    return Array.from(stateContainer.querySelectorAll(`${STATE_SELECTOR}:checked`));
                }

                return Array.from(stateContainer.querySelectorAll(STATE_SELECTOR));
            }

            if (stateControl.type === 'checkbox' || stateControl.type === 'radio') {
                return stateControl.checked ? [stateControl] : [];
            }

            return [stateControl];
        }

        /**
         * @param  {Element} stateControl
         * @param  {?string} [stateLocale]
         * @param  {Element|Document} [stateContainer]
         * @return {(boolean|number|string)[]}
         */
        findStateValues(stateControl, stateLocale = null, stateContainer = document) {
            const inputs = this.findStateInputs(stateControl, stateLocale, stateContainer);
            const values = inputs.map((input) => {
                if (input.type === 'checkbox' || input.type === 'radio') {
                    if (!input.hasAttribute('value') || input.value === '') {
                        return input.name ?? input.id;
                    }
                }

                return input.value;
            }).filter((value) => (value != null));

            return Array.from(new Set(values));
        }

        // Reset the value of the given element.
        resetElementValue(element) {
            if (!element || !(element instanceof Element)) {
                return;
            }

            element.querySelectorAll('input,select,textarea').forEach(
                (control) => {
                    if (control.attributes.getNamedItem('readonly')) {
                        return;
                    }

                    if (control.type === 'checkbox' || control.type === 'radio') {
                        control.checked = false;
                        $(control).closest('.btn').removeClass('active');
                    } else if (control.type === 'number') {
                        control.value = 0;
                    } else if (control instanceof HTMLSelectElement) {
                        control.value = '';
                        Array.from(control.options).forEach((opt) => {
                            opt.selected = false;
                        });
                        // Handle bootstrap-select value reset
                        if ($(control).parent().hasClass('bootstrap-select') || $(control).data('selectpicker')) {
                            $(control).selectpicker('val', null);
                            $(control).selectpicker('refresh');
                        }
                    } else {
                        control.value = '';
                    }

                    // Handle selectize reset if present
                    if (control.selectize) {
                        control.selectize.clear();
                    }

                    const changeEvent = new Event('change', { bubbles: true });
                    control.dispatchEvent(changeEvent);
                }
            );
        }

        /**
         * Resolves the visibility/requirement of the target's condition
         * against the given state.
         *
         * @param  {Element}      targetElement
         * @param  {RegExp}       targetRegExp
         * @param  {StateContext} stateContext
         * @return boolean
         */
        handleStateOnTarget(targetElement, targetRegExp, stateContext) {
            DEBUG && console.group('ConditionalLogic.handleStateOnTarget:', targetElement);
            DEBUG && console.log('Context:', stateContext);

            const isParentTargetHidden = Boolean(
                targetElement.parentElement?.closest(`[class*="${TARGET_SELECTOR_PREFIX}"].d-none`)
            );
            DEBUG && console.log('isParentTargetHidden:', isParentTargetHidden);

            let stateLayoutContainer = stateContext.control?.parentElement?.closest(`[class*="col-md-"]`);
            let layoutIndex = null;
            if (stateLayoutContainer) {
                if (!stateLayoutContainer.hasAttribute('data-initial-layout')) {
                    let match = Array.from(stateLayoutContainer.classList).join(':').match(/col-md-([0-9]*)/);
                    if (match) {
                        stateLayoutContainer.setAttribute('data-initial-layout', match[1]);
                    }
                }
                layoutIndex = stateLayoutContainer.getAttribute('data-initial-layout');
            }

            // This logic for layout fractionning can become jank depending on use cases
            let layoutFraction = null;

            let layoutMatches = Array.from(targetElement.classList)
                .join(':')
                .match(`${LAYOUT_SELECTOR_PREFIX}-([0-9]*)`);

            if (layoutMatches) {
                layoutFraction = layoutMatches[1];
            }

            const wasHidden = targetElement.classList.contains('d-none');

            let show = false;
            const COL_REGEXP = new RegExp(/col-md-[0-9]*/, 'g');
            this.withTargetMatch(targetElement, targetRegExp, (_className, targetValue, negate, allowEmpty) => {
                const isEmpty = !stateContext.values.length || stateContext.values.includes('');

                if (negate) {
                    const hasMatchedValue = stateContext.values.includes(targetValue);
                    const shouldHide = isParentTargetHidden || hasMatchedValue || (!allowEmpty && isEmpty);

                    if (shouldHide) {
                        this.resetElementValue(targetElement);
                        targetElement.classList.add('d-none');
                        if (layoutFraction && targetElement.parentElement) {
                            targetElement.parentElement.className = targetElement.parentElement.className.replace(COL_REGEXP, ' ');
                        }
                        if (stateLayoutContainer && layoutIndex) {
                            stateLayoutContainer.className = stateLayoutContainer.className.replace(COL_REGEXP, 'col-md-' + layoutIndex);
                        }
                    } else {
                        targetElement.classList.remove('d-none');
                        if (layoutFraction && targetElement.parentElement) {
                            targetElement.parentElement.classList.add('col-md-' + layoutFraction);
                        }
                        if (stateLayoutContainer && layoutIndex && layoutFraction) {
                            stateLayoutContainer.className = stateLayoutContainer.className.replace(COL_REGEXP, 'col-md-' + (layoutIndex - layoutFraction));
                        }
                        show = true;
                    }
                } else {
                    if (!isParentTargetHidden && (stateContext.values.includes(targetValue) || stateContext.values === targetValue)) {
                        targetElement.classList.remove('d-none');
                        if (layoutFraction && targetElement.parentElement) {
                            targetElement.parentElement.classList.add('col-md-' + layoutFraction);
                        }
                        if (stateLayoutContainer && layoutIndex && layoutFraction) {
                            stateLayoutContainer.className = stateLayoutContainer.className.replace(COL_REGEXP, 'col-md-' + (layoutIndex - layoutFraction));
                        }
                        stateContext.triggeredTarget = true;
                        show = true;
                    } else {
                        this.resetElementValue(targetElement);
                        targetElement.classList.add('d-none');
                        if (layoutFraction && targetElement.parentElement) {
                            targetElement.parentElement.className = targetElement.parentElement.className.replace(COL_REGEXP, ' ');
                        }
                        if (!stateContext.triggeredTarget && stateLayoutContainer && layoutIndex) {
                            stateLayoutContainer.className = stateLayoutContainer.className.replace(COL_REGEXP, 'col-md-' + layoutIndex);
                        }
                    }
                }
            });

            DEBUG && console.log('Show:', show);

            const requiredElements = targetElement.querySelectorAll(':required,[data-condition-required]');

            requiredElements.forEach((requiredElement) => {
                if (!requiredElement.hasAttribute('data-condition-required')) {
                    requiredElement.setAttribute('data-condition-required', requiredElement.hasAttribute('required') ? 'true' : 'false');
                }

                if (show && requiredElement.getAttribute('data-condition-required') === 'true') {
                    requiredElement.toggleAttribute('required', true);
                } else {
                    requiredElement.toggleAttribute('required', false);
                }
            });

            const isHidden = targetElement.classList.contains('d-none');
            if (wasHidden !== isHidden) {
                this.refreshTargetsAndControlsInNode(targetElement);
            }

            DEBUG && console.groupEnd();
        }

        /**
         * @param  Element element
         * @return boolean
         */
        isFormControl(element) {
            if (element instanceof HTMLInputElement) {
                return true;
            }

            if (element instanceof HTMLSelectElement) {
                return true;
            }

            if (element instanceof HTMLTextAreaElement) {
                return true;
            }

            return false;
        }

        /**
         * Applies the callback to the target's condition against the given state.
         *
         * @param  {Element}  targetElement
         * @param  {RegExp}   targetRegExp
         * @param  {Function} callback
         * @throws {TypeError} if callback is not a function.
         */
        withTargetMatch(targetElement, targetRegExp, callback) {
            if (typeof callback !== 'function') {
                throw new TypeError(
                    'ConditionalLogic.withTargetMatch expected callback argument to be a function'
                );
            }

            for (const className of targetElement.classList) {
                const matches = className.match(targetRegExp);
                if (!matches) {
                    continue;
                }

                let [ , notEqual, allowEmptyPrefix, targetValue, allowEmptySuffix ] = matches;
                if (!targetValue) {
                    continue;
                }

                const negate     = (notEqual === '!');
                const allowEmpty = Boolean(allowEmptyPrefix || allowEmptySuffix);

                if (targetValue === 'null') {
                    targetValue = '';
                }

                /**
                 * @param  {string}  className
                 * @param  {string}  targetValue
                 * @param  {boolean} negate
                 * @param  {boolean} allowEmpty
                 * @return {?boolean}
                 */
                const result = callback(className, targetValue, negate, allowEmpty);
                if (result === true) {
                    break;
                }
            }
        }
    }

    class StateContext {
        /**
         * @param {string}                    key
         * @param {(boolean|number|string)[]} values
         * @param {Element}                   control
         * @param {?Element}                  container
         */
        constructor(key, values, control, container = null) {
            this.key       = key;
            this.values    = values;
            this.control   = control;
            this.container = container;
            this.triggeredTarget = false;
        }
    }

    Charcoal.Admin.conditionalLogic = new ConditionalLogic;

})(Charcoal, jQuery);
